/**
 * Générateur — Connaître et utiliser la distributivité simple sur des exemples
 * numériques : k(a + b) = ka + kb et k(a − b) = ka − kb.
 * Attendu ID : 698 | Niveau : 5ème | Domaine : Opérations (cycle 4 rénové, cluster 5)
 *
 * Plan de cartes (validé par le responsable pédagogique) :
 *
 *   Carte « reconnaitre » — Ex.3 Relier les expressions égales
 *     Format : relier (4 paires). K, A grands (2 chiffres) par construction —
 *     le « sans effectuer de calculs » du doc est garanti : les produits sont
 *     infaisables de tête, seule la STRUCTURE permet d'apparier.
 *
 *   Carte « factoriser » — Ex.9 Calculs astucieux (factoriser)
 *     Format : texte_trou_saisie, étayage dégressif en rotation (4 gabarits,
 *     style feuille ligne à ligne). La somme/différence des coefficients tombe
 *     TOUJOURS sur 100 (ou 50) par construction → la factorisation mène à un
 *     ×100 (A5, conforme au doc).
 *
 *   Carte « developper » — Ex.11 Calcule astucieusement (développer)
 *     Formats : texte_trou_saisie (étapes), qcm_unique (rotation :
 *     décomposition astucieuse / résultat). Bases 100±δ et 1000±δ — les DEUX
 *     cas k(a+b) et k(a−b) tournent. Facteur entier ou décimal (1,7 × 1001).
 *
 *   Carte « mimi » — Ex.4 Les dépenses de Mimi (le sens concret)
 *     Formats : texte_trou_saisie (les DEUX chemins en parallèle dans la même
 *     phrase — l'égalité des chemins EST la distributivité), qcm_unique
 *     (question c fermée). Prix complémentaires à l'entier par construction.
 *
 *   Carte « aire » (hors quota) — Ex.5 + Ex.6 L'aire de deux façons (figures)
 *     Format : texte_trou_saisie avec figure SVG, rotation entre :
 *       - cas k(a+b) : rectangle découpé en deux (Ex.5) ;
 *       - cas k(a−b) : carré + rectangle bleu (Ex.6).
 *     Les DEUX méthodes dans la même phrase (arbitrage utilisateur). Les
 *     dimensions figurent AUSSI dans le texte (accessibilité + solveur).
 */

import {
    randomChoice, randInt,
    makeQcm, makeTexteTrou, makeRelier,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 698;

const L_RECO = 'Reconnaître les expressions égales (sans calculer)';
const L_FACT = 'Calculs astucieux : factoriser';
const L_DEV  = 'Calculs astucieux : développer';
const L_MIMI = 'Les dépenses de la semaine (deux chemins)';
const L_AIRE = "L'aire de deux façons (figure)";

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

/** Dixièmes → écriture FR. */
const dixToFr = (d) => (d % 10 === 0 ? String(d / 10) : `${Math.floor(d / 10)},${d % 10}`);

/** Centimes → écriture FR sans zéros finaux. */
const centToFr = (c) => {
    const ent = Math.floor(c / 100);
    const cents = c % 100;
    if (cents === 0) return String(ent);
    return `${ent},${String(cents).padStart(2, '0').replace(/0+$/, '')}`;
};

/** Déduplique des choix de QCM (rotation par mélange). */
const dedupeChoix = (bonne, candidats, complements) => {
    const norm = (s) => String(s).replace(',', '.').trim();
    const vus = new Set([norm(bonne)]);
    const retenus = [];
    [...candidats.sort(() => Math.random() - 0.5), ...complements].forEach(c => {
        const n = norm(c);
        if (!vus.has(n) && retenus.length < 3) { vus.add(n); retenus.push(String(c)); }
    });
    return retenus;
};

// ═══════════════════════════════════════════════════════════
// Carte 1 — Reconnaître les expressions égales
// ═══════════════════════════════════════════════════════════

const genReconnaitreRelier = () => {
    const K = randInt(61, 97);
    let A = randInt(31, 59);
    if (A === K) A += 1;
    const b = randInt(3, 9);
    const pairs = [
        { source: `${K} × (${A} − ${b})`, target: `${K} × ${A} − ${K} × ${b}` },
        { source: `${A} × ${K} − ${A} × ${b}`, target: `${A} × (${K} − ${b})` },
        { source: `${K} × (${A} + ${b})`, target: `${K} × ${A} + ${K} × ${b}` },
        { source: `${A} × ${K} + ${A} × ${b}`, target: `${A} × (${K} + ${b})` },
    ];
    const texte = '<p><strong>Sans effectuer de calculs</strong>, relie les expressions ' +
        'qui conduisent au même résultat.</p>';
    return makeRelier(pairs, texte, opts(L_RECO));
};

// ═══════════════════════════════════════════════════════════
// Carte 2 — Factoriser (étayage dégressif)
// ═══════════════════════════════════════════════════════════

/** 4 gabarits — la factorisation tombe sur ×100 (ou ×50 → 25) par construction. */
const GABARITS_FACT = [
    (l) => {   // a. pré-structuré : (100+y)×k − y×k
        const y = randInt(3, 30);
        const k = randInt(12, 49);
        return `${l} = ${100 + y} × ${k} − ${y} × ${k}\n` +
            `${l} = ({{${100 + y}}} − {{${y}}}) × ${k}\n` +
            `${l} = {{100}} × ${k}\n` +
            `${l} = {{${100 * k}}}`;
    },
    (l) => {   // b. p×k + k×q avec p + q = 100
        const p = randInt(11, 89);
        const k = randInt(12, 49);
        return `${l} = ${p} × ${k} + ${k} × ${100 - p}\n` +
            `${l} = ${k} × ({{${p}}} + {{${100 - p}}})\n` +
            `${l} = ${k} × {{100}}\n` +
            `${l} = {{${100 * k}}}`;
    },
    (l) => {   // c. trois termes × 0,5, somme = 50 → 25 (x ≥ 15 par bornes)
        const y = randInt(5, 20);
        const z = randInt(10, 40);
        const x = 50 + y - z;
        return `${l} = ${x} × 0,5 − ${y} × 0,5 + ${z} × 0,5\n` +
            `${l} = ({{${x}}} − {{${y}}} + {{${z}}}) × 0,5\n` +
            `${l} = {{50}} × 0,5\n` +
            `${l} = {{25}}`;
    },
    (l) => {   // d. étayage minimal : k décimal × (a + b − c = 100)
        let kC = randInt(111, 899);
        if (kC % 10 === 0) kC += randomChoice([1, 3, 7]);
        // c = a + b − 100 ∈ [5..60] par CONSTRUCTION : on tire c puis b, a s'en déduit
        const c = randInt(5, 60);
        const b = randInt(10, 60);
        const a = 100 + c - b;                       // ∈ [45..150], toujours valide
        const kF = centToFr(kC);
        return `${l} = ${a} × ${kF} + ${kF} × ${b} − ${kF} × ${c}\n` +
            `${l} = ${kF} × ({{${a}}} + {{${b}}} − {{${c}}})\n` +
            `${l} = {{${centToFr(kC * 100)}}}`;
    },
];

const genFactoriserTrous = () => {
    const i = randInt(0, 3);
    const lettre = ['A', 'B', 'C', 'D'][i];
    const phrase = GABARITS_FACT[i](lettre);
    const texte = '<p>Effectue ce calcul <strong>de manière astucieuse</strong> : ' +
        'le facteur commun apparaît dans chaque produit. Complète :</p>';
    return makeTexteTrou(phrase, texte, { ...opts(L_FACT), mode: 'saisie' });
};

// ═══════════════════════════════════════════════════════════
// Carte 3 — Développer
// ═══════════════════════════════════════════════════════════

/** k × (100±δ) ou k × (1000±δ) — les deux cas +/− tournent. */
const tirerDevelopper = () => {
    const grand = randomChoice([true, false]);
    const cent = grand ? 1000 : 100;
    const delta = grand ? randomChoice([1, 2, 10]) : randomChoice([1, 2, 3]);
    const plus = randomChoice([true, false]);
    const base = plus ? cent + delta : cent - delta;
    // Facteur entier, ou décimal 1 déc pour les grandes bases (1,7 × 1 001)
    const decimal = grand && randomChoice([true, false]);
    const k10 = decimal ? randInt(11, 29) : randInt(12, 29) * 10;
    const kF = dixToFr(k10);
    const res10 = k10 * base;                     // dixièmes, exact
    return { cent, delta, plus, base, kF, resF: dixToFr(res10), k10 };
};

const genDevelopperTrous = () => {
    const t = tirerDevelopper();
    const signe = t.plus ? '+' : '−';
    const phrase =
        `${t.kF} × ${t.base} = ${t.kF} × (${t.cent} ${signe} {{${t.delta}}})\n` +
        `${t.kF} × ${t.base} = ${t.kF} × ${t.cent} ${signe} ${t.kF} × {{${t.delta}}}\n` +
        `${t.kF} × ${t.base} = {{${t.resF}}}`;
    const texte = '<p>Calcule <strong>astucieusement</strong>, en décomposant. ' +
        'Complète (écris les nombres sans zéro inutile, ex. : 12,5) :</p>';
    return makeTexteTrou(phrase, texte, { ...opts(L_DEV), mode: 'saisie' });
};

const genDevelopperQcm = () => {
    const t = tirerDevelopper();
    const signe = t.plus ? '+' : '−';
    const signeOpp = t.plus ? '−' : '+';
    if (randomChoice([true, false])) {
        // Choisir la décomposition astucieuse
        const bonne = `${t.kF} × (${t.cent} ${signe} ${t.delta})`;
        const pieges = [
            `${t.kF} × (${t.cent} ${signeOpp} ${t.delta})`,     // mauvais signe
            `${t.kF} × ${t.cent} ${signe} ${t.delta}`,          // n'a distribué qu'à moitié
            `(${t.kF} ${signe} ${t.delta}) × ${t.cent}`,        // décompose le mauvais facteur
        ];
        const texte = `<p>Pour calculer astucieusement <strong>${t.kF} × ${t.base}</strong>, ` +
            'quelle décomposition faut-il choisir ?</p>';
        return makeQcm([bonne, ...pieges], bonne, texte, opts(L_DEV));
    }
    // Le résultat
    const bonne = t.resF;
    const demiDistribue = dixToFr(t.k10 * t.cent + (t.plus ? 1 : -1) * t.delta * 10);
    const mauvaisSigne = dixToFr(t.k10 * (t.plus ? t.cent - t.delta : t.cent + t.delta));
    const distracteurs = dedupeChoix(bonne, [demiDistribue, mauvaisSigne],
        [dixToFr(t.k10 * t.base + 100), dixToFr(t.k10 * t.base - 100)]);
    const texte = `<p>Calcule astucieusement : <strong>${t.kF} × ${t.base}</strong></p>`;
    return makeQcm([bonne, ...distracteurs], bonne, texte, opts(L_DEV));
};

// ═══════════════════════════════════════════════════════════
// Carte 4 — Les dépenses de la semaine (deux chemins)
// ═══════════════════════════════════════════════════════════

const CTX_MIMI = [
    { qui: 'Mimi', a1: 'une boisson', a1s: 'les boissons', a2: 'un sandwich', a2s: 'les sandwichs' },
    { qui: 'Léo', a1: 'un jus de fruits', a1s: 'les jus de fruits', a2: 'une salade', a2s: 'les salades' },
    { qui: 'Ana', a1: 'un lait chaud', a1s: 'les laits chauds', a2: 'une brioche', a2s: 'les brioches' },
];

/** Prix complémentaires à l'entier (0,90 + 2,10 = 3) → tout exact. */
const tirerMimi = () => {
    const n = randInt(4, 6);
    const tC = randomChoice([200, 300, 400]);            // total quotidien (centimes)
    const bC = randomChoice([60, 70, 80, 90, 110, 120, 130].filter(v => v < tC - 50));
    const sC = tC - bC;
    return { n, bC, sC, tC, ctx: randomChoice(CTX_MIMI) };
};

const enonceMimi = ({ n, bC, sC, ctx }) =>
    `<p><strong>${n} jours par semaine</strong>, ${ctx.qui} achète ${ctx.a1} à ` +
    `<strong>${centToFr(bC)} €</strong> et ${ctx.a2} à <strong>${centToFr(sC)} €</strong>.</p>`;

const genMimiTrous = () => {
    const t = tirerMimi();
    const { n, bC, sC, tC, ctx } = t;
    const [nb, ns, nt, tF] = [centToFr(n * bC), centToFr(n * sC), centToFr(n * tC), centToFr(tC)];
    const phrase =
        `Chemin 1 — Dépense pour ${ctx.a1s} : ${n} × ${centToFr(bC)} = {{${nb}}} €\n` +
        `Dépense pour ${ctx.a2s} : ${n} × ${centToFr(sC)} = {{${ns}}} €\n` +
        `Dépense totale : {{${nb}}} + {{${ns}}} = {{${nt}}} €\n` +
        `Chemin 2 — Dépense quotidienne : ${centToFr(bC)} + ${centToFr(sC)} = {{${tF}}} €\n` +
        `Dépense hebdomadaire : ${n} × {{${tF}}} = {{${nt}}} €`;
    const texte = enonceMimi(t) +
        '<p>Calcule la dépense hebdomadaire par les deux chemins. ' +
        'Complète (écris les nombres sans zéro inutile, ex. : 12,5) :</p>';
    return makeTexteTrou(phrase, texte, { ...opts(L_MIMI), mode: 'saisie' });
};

const genMimiQcm = () => {
    const t = tirerMimi();
    const { n, bC, sC, tC } = t;
    const [bF, sF, ntF] = [centToFr(bC), centToFr(sC), centToFr(n * tC)];
    const bonne = `Les deux chemins donnent ${ntF} €, car ` +
        `${n} × ${bF} + ${n} × ${sF} = ${n} × (${bF} + ${sF})`;
    const pieges = [
        'C\'est un hasard si les deux chemins donnent le même résultat',
        'Le chemin 1 donne toujours un résultat plus grand que le chemin 2',
        `Les deux chemins donnent des résultats différents : il faut toujours utiliser le chemin 1`,
    ];
    const texte = enonceMimi(t) +
        '<p>On calcule la dépense hebdomadaire soit en séparant les deux achats (chemin 1), ' +
        'soit en partant de la dépense quotidienne (chemin 2). Que peut-on dire ?</p>';
    return makeQcm([bonne, ...pieges], bonne, texte, opts(L_MIMI));
};

// ═══════════════════════════════════════════════════════════
// Carte 5 — L'aire de deux façons (figures)
// ═══════════════════════════════════════════════════════════

const imageElement = (svg) => ({
    version: 1,
    elements: [{
        type: 'image',
        config: { imageUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`, maxWidth: 'compact' },
    }],
});

/** Ex.5 — rectangle découpé (cas k(a+b)) : a + b entier par construction. */
const svgRectangleDecoupe = (a10, b10, h) => {
    const s = 34;
    const wa = a10 * s / 10, wb = b10 * s / 10, hh = h * s;
    const x0 = 60, y0 = 40;
    const W = Math.round(x0 + wa + wb + 40), H = Math.round(y0 + hh + 30);
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" font-family="sans-serif">` +
        `<rect x="${x0}" y="${y0}" width="${wa + wb}" height="${hh}" fill="none" stroke="#1E3A8A" stroke-width="2"/>` +
        `<line x1="${x0 + wa}" y1="${y0}" x2="${x0 + wa}" y2="${y0 + hh}" stroke="#1E3A8A" stroke-width="1.4" stroke-dasharray="5 4"/>` +
        `<text x="${x0 + wa / 2}" y="${y0 - 10}" text-anchor="middle" font-size="14" fill="#0f172a">${dixToFr(a10)} cm</text>` +
        `<text x="${x0 + wa + wb / 2}" y="${y0 - 10}" text-anchor="middle" font-size="14" fill="#0f172a">${dixToFr(b10)} cm</text>` +
        `<text x="${x0 - 10}" y="${y0 + hh / 2 + 5}" text-anchor="end" font-size="14" fill="#0f172a">${h} cm</text>` +
        '</svg>';
};

/** Ex.6 — carré + rectangle bleu (cas k(a−b)). */
const svgCarrePlusBleu = (L, c10) => {
    const s = 34;
    const wc = c10 * s / 10, w = L * s, hh = wc;
    const x0 = 64, y0 = 40;
    const W = Math.round(x0 + w + 40), H = Math.round(y0 + hh + 34);
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" font-family="sans-serif">` +
        `<rect x="${x0}" y="${y0}" width="${wc}" height="${hh}" fill="#E2E8F0" stroke="#1E3A8A" stroke-width="2"/>` +
        `<rect x="${x0 + wc}" y="${y0}" width="${w - wc}" height="${hh}" fill="#60A5FA" stroke="#1E3A8A" stroke-width="2"/>` +
        `<text x="${x0 - 10}" y="${y0 + hh / 2 + 5}" text-anchor="end" font-size="14" fill="#0f172a">${dixToFr(c10)} cm</text>` +
        `<text x="${x0 + w / 2}" y="${y0 + hh + 22}" text-anchor="middle" font-size="14" fill="#0f172a">${L} cm</text>` +
        '</svg>';
};

const genAireTrous = () => {
    if (randomChoice([true, false])) {
        // Cas k(a + b) : rectangle découpé — a + b entier
        let a10 = randInt(21, 79);
        if (a10 % 10 === 0) a10 += randomChoice([1, 3, 7]);
        const b10 = 10 - (a10 % 10);
        const T = (a10 + b10) / 10;
        const h = randInt(2, 4);
        const [aF, bF] = [dixToFr(a10), dixToFr(b10)];
        const phrase =
            `Méthode 1 : ({{${aF}}} + {{${bF}}}) × ${h} = {{${T}}} × ${h} = {{${T * h}}} cm²\n` +
            `Méthode 2 : ${aF} × ${h} + ${bF} × ${h} = {{${dixToFr(a10 * h)}}} + {{${dixToFr(b10 * h)}}} = {{${T * h}}} cm²`;
        const texte = '<p>Le rectangle ci-dessous est découpé en deux parties : ' +
            `les largeurs mesurent <strong>${aF} cm</strong> et <strong>${bF} cm</strong>, ` +
            `la hauteur mesure <strong>${h} cm</strong>.</p>` +
            '<p>Calcule son aire totale <strong>de deux façons différentes</strong>. ' +
            'Complète (écris les nombres sans zéro inutile, ex. : 12,5) :</p>';
        const payload = makeTexteTrou(phrase, texte, { ...opts(L_AIRE), mode: 'saisie' });
        payload.elements_additionnels = imageElement(svgRectangleDecoupe(a10, b10, h));
        return payload;
    }
    // Cas k(L − c) : carré + rectangle bleu
    const L = randInt(5, 9);
    let c10 = randInt(21, Math.min(44, L * 10 - 12));
    if (c10 % 10 === 0) c10 += randomChoice([1, 3]);
    const cF = dixToFr(c10);
    const resC = (L * 10 - c10) * c10;               // centièmes
    const phrase =
        `Méthode 1 : ({{${L}}} − {{${cF}}}) × ${cF} = {{${dixToFr(L * 10 - c10)}}} × ${cF} = {{${centToFr(resC)}}} cm²\n` +
        `Méthode 2 : ${L} × ${cF} − ${cF} × ${cF} = {{${dixToFr(L * c10)}}} − {{${centToFr(c10 * c10)}}} = {{${centToFr(resC)}}} cm²`;
    const texte = '<p>La figure est formée d\'un carré (gris) et d\'un rectangle bleu : ' +
        `la longueur totale est <strong>${L} cm</strong> et le côté du carré mesure ` +
        `<strong>${cF} cm</strong>.</p>` +
        '<p>Calcule l\'aire du rectangle bleu <strong>de deux façons différentes</strong>. ' +
        'Complète (écris les nombres sans zéro inutile, ex. : 12,5) :</p>';
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_AIRE), mode: 'saisie' });
    payload.elements_additionnels = imageElement(svgCarrePlusBleu(L, c10));
    return payload;
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    reconnaitre_relier:          genReconnaitreRelier,
    factoriser_texte_trou_saisie: genFactoriserTrous,
    developper_texte_trou_saisie: genDevelopperTrous,
    developper_qcm_unique:       genDevelopperQcm,
    mimi_texte_trou_saisie:      genMimiTrous,
    mimi_qcm_unique:             genMimiQcm,
    aire_texte_trou_saisie:      genAireTrous,
};

export const CATEGORIES = [
    {
        id: 'reconnaitre', label: L_RECO,
        formatsList: [{ key: 'reconnaitre_relier', label: 'Relier' }],
    },
    {
        id: 'factoriser', label: L_FACT,
        formatsList: [{ key: 'factoriser_texte_trou_saisie', label: 'Saisie trous' }],
    },
    {
        id: 'developper', label: L_DEV,
        formatsList: [
            { key: 'developper_texte_trou_saisie', label: 'Saisie trous' },
            { key: 'developper_qcm_unique',        label: 'QCM' },
        ],
    },
    {
        id: 'mimi', label: L_MIMI,
        formatsList: [
            { key: 'mimi_texte_trou_saisie', label: 'Saisie trous' },
            { key: 'mimi_qcm_unique',        label: 'QCM' },
        ],
    },
    {
        id: 'aire', label: L_AIRE,
        formatsList: [{ key: 'aire_texte_trou_saisie', label: 'Saisie trous' }],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    if (!payload._meta.generatorKey) payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    dixToFr, centToFr, dedupeChoix,
    GABARITS_FACT, tirerDevelopper, tirerMimi,
    svgRectangleDecoupe, svgCarrePlusBleu,
};
